import React, { useState, useRef, useEffect, useCallback } from "react";
import AIZzra from "../assets/AI.png";
import { NETWORKS } from "../constants/networks";
import { cancelReasons } from "../data/services";
import { COMPLAINT_TYPES } from "../constants/complaintTypes";
import { FEEDBACK_RATINGS, FEEDBACK_CATEGORIES } from "../constants/feedbackOptions";
import useOtpFlow from "../hooks/useOtpFlow";
import useCancelSubscription, { getReasonLabel } from "../hooks/useCancelSubscription";
import { fetchSubscriptions } from "../services/subscriptionApi";
import { apiUrl } from "../config/apiConfig";
import "./ZaraChatWidget.css";
import { useNavigate } from "react-router-dom";

let bubbleIdCounter = 0;
const nextId = () => `msg-${Date.now()}-${bubbleIdCounter++}`;

const ZaraChatWidget = ({
  isEn = true,
  onMenuSelect,
  children,
  initialPhone = "",
  initialNetwork = "",
  skipAuth = false,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("phone");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);

  const [phoneDraft, setPhoneDraft] = useState("");
  const [phone, setPhone] = useState("");
  const [network, setNetwork] = useState("");
  const [otpDraft, setOtpDraft] = useState("");
  const [freeTextDraft, setFreeTextDraft] = useState("");

  const [servicesList, setServicesList] = useState([]);
  const [cancelSvc, setCancelSvc] = useState(null);

  const [complaintSvc, setComplaintSvc] = useState(null);
  const [complaintType, setComplaintType] = useState(null);
  const [complaintDescDraft, setComplaintDescDraft] = useState("");
  const [complaintError, setComplaintError] = useState("");

  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackCategory, setFeedbackCategory] = useState(null);
  const [feedbackCommentDraft, setFeedbackCommentDraft] = useState("");

  const bodyRef = useRef(null);
  const otp = useOtpFlow();
  const cancelFlow = useCancelSubscription();
  const navigate = useNavigate();

  const text = {
    greeting: isEn ? "Assalam-o-Alaikum! I'm ZARA 👋 What's your mobile number?" : "Assalam-o-Alaikum! Main ZARA hoon 👋 Apna mobile number bata dein.",
    welcomeBack: (p) => (isEn ? `Hi again! Let me pull up the active services for ${p}...` : `Assalam-o-Alaikum dobara! Main ${p} ki active services dekhti hoon...`),
    phoneInvalid: isEn ? "Please enter a valid mobile number." : "Barah-e-karam valid mobile number enter karein.",
    thanksPhone: (p) => (isEn ? `Thank you! Which network or wallet is ${p} on?` : `Shukriya! ${p} kis network ya wallet par hai?`),
    sendingOtp: isEn ? "Sending you a one-time code, one sec... 🔐" : "Aapko OTP bhej rahi hoon, ek second... 🔐",
    otpSentTo: (p) => (isEn ? `Done! I've sent a code to ${p}. Please enter it below.` : `Ho gaya! ${p} par code bhej diya hai. Neeche enter karein.`),
    otpSendFailed: isEn ? "I couldn't send the OTP. Let's try again." : "OTP bhejne mein masla aya. Dobara koshish karte hain.",
    otpInvalid: isEn ? "Please enter the OTP." : "Barah-e-karam OTP enter karein.",
    otpExpired: isEn ? "This code expired. Tap resend for a new one." : "Ye code expire ho gaya. Resend par tap karein.",
    verifying: isEn ? "Verifying your code... ⏳" : "Aap ka code verify kar rahi hoon... ⏳",
    verifyFailed: isEn ? "That code didn't match. Please try again." : "Ye code match nahi hua. Dobara koshish karein.",
    verified: isEn ? "You're verified ✅ Let me pull up your active services..." : "Verify ho gaya ✅ Ab main aap ki active services dekhti hoon...",
    resend: isEn ? "Resend code" : "Dobara bhejein",
    noServices: isEn ? "Looks like you don't have any active services on this number right now." : "Is number par filhal koi active service nahi hai.",
    servicesFailed: isEn ? "I couldn't load your services right now, but let's continue." : "Services load nahi ho sakin, lekin hum aage barh sakte hain.",
    menuPrompt: isEn ? "What would you like help with today?" : "Aaj main aap ki kis cheez mein madad kar sakti hoon?",

    // Cancel
    noServiceToCancel: isEn ? "You don't have any active service to cancel right now." : "Filhal aap ke paas cancel karne ke liye koi active service nahi hai.",
    whichServiceCancel: isEn ? "Which service would you like to cancel?" : "Aap konsi service cancel karna chahte hain?",
    whyCancel: (name) => (isEn ? `Got it — ${name}. Why are you cancelling it?` : `Theek hai — ${name}. Aap ye kyun cancel kar rahe hain?`),
    cancelling: isEn ? "Cancelling your subscription and processing your refund... 🔄" : "Aap ki subscription cancel kar rahi hoon aur refund process kar rahi hoon... 🔄",
    cancelFailed: isEn ? "I couldn't complete the cancellation. Please try again." : "Cancel karne mein masla aya. Dobara koshish karein.",
    cancelClosing: (name) => (isEn
      ? `${name} has been successfully cancelled and removed from your active services. You won't be charged again.`
      : `${name} kamyabi se cancel ho gayi hai aur aap ki active services se hata di gayi hai. Ab aap se dobara charge nahi hoga.`),

    // Complaint
    whichServiceComplaint: isEn ? "Which service is this complaint related to?" : "Yeh complaint konsi service se related hai?",
    generalComplaint: isEn ? "General / Not service specific" : "General / Kisi khaas service se nahi",
    whatWentWrong: isEn ? "I'm sorry to hear that. What went wrong?" : "Mujhe afsos hai. Kya masla pesh aya?",
    describeIssue: (label) => (isEn
      ? `Got it — ${label}. Please describe the issue in a bit more detail.`
      : `Theek hai — ${label}. Barah-e-karam masla thora tafseel se bataein.`),
    submittingComplaint: isEn ? "Registering your complaint... 📝" : "Aap ki complaint register kar rahi hoon... 📝",
    complaintFailed: isEn ? "I couldn't register your complaint. Please try again." : "Complaint register nahi ho saki. Dobara koshish karein.",
    complaintDescRequired: isEn ? "Please describe the issue so our team can look into it." : "Barah-e-karam masla likhein taake hamari team dekh sake.",
    complaintClosing: (id) => (isEn
      ? `Thank you for bringing this to our attention. Your complaint (#${id}) has been forwarded to the concerned team, and we sincerely apologise for the inconvenience caused.`
      : `Is masle ko hamare sath share karne ka shukriya. Aap ki complaint (#${id}) concerned team ko bhej di gayi hai, aur is takleef ke liye hum maazrat khwah hain.`),

    // Feedback
    whatIsThisAbout: isEn ? "Thank you! What is this feedback mainly about?" : "Shukriya! Yeh feedback zyada tar kis baray mein hai?",
    anyComments: (label) => (isEn
      ? `Got it — ${label}. Would you like to add any comments? (optional)`
      : `Theek hai — ${label}. Kya aap koi comment add karna chahenge? (optional)`),
    submittingFeedback: isEn ? "Submitting your feedback... 💚" : "Aap ki feedback submit kar rahi hoon... 💚",
    feedbackFailed: isEn ? "I couldn't submit your feedback. Please try again." : "Feedback submit nahi ho saki. Dobara koshish karein.",
    feedbackClosing: isEn
      ? "We really appreciate you taking the time to share this with us — it helps us serve you better."
      : "Waqt nikal kar hamare sath yeh share karne ka bohat shukriya — is se hum aap ko behtar service de sakenge.",
    skipComment: isEn ? "Skip, just submit" : "Skip karein, bas submit karein",

    // End of chat
    sessionEnded: isEn
      ? "That's all for now! If you need anything else, feel free to open a new chat with me anytime. 😊"
      : "Filhal itna hi! Agar aage kabhi madad chahiye ho to kabhi bhi mujh se naya chat shuru kar lein. 😊",
    closeChatBtn: isEn ? "Close Chat" : "Chat Band Karein",
  };

  const addMessage = (sender, payload) => {
    setMessages((prev) => [...prev, { id: nextId(), sender, ...payload }]);
  };

  const pushAiText = (msg, delay = 550) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        addMessage("ai", { type: "text", text: msg });
        resolve();
      }, delay);
    });
  };

  const pushAiServices = (services, delay = 500) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        addMessage("ai", { type: "services", services });
        resolve();
      }, delay);
    });
  };

  const pushAiCancelResult = (svc, refundTotal, delay = 400) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        addMessage("ai", { type: "cancelResult", svc, refundTotal });
        resolve();
      }, delay);
    });
  };

  const pushAiComplaintResult = (complaintId, typeLabel, delay = 400) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        addMessage("ai", { type: "complaintResult", complaintId, typeLabel });
        resolve();
      }, delay);
    });
  };

  const pushAiFeedbackResult = (ratingObj, categoryLabel, message, delay = 400) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        addMessage("ai", { type: "feedbackResult", ratingObj, categoryLabel, message });
        resolve();
      }, delay);
    });
  };

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing]);

  useEffect(() => {
    if (step === "otp" && otp.otpSent && otp.timerSeconds === 0) {
      addMessage("ai", { type: "text", text: `⚠️ ${text.otpExpired}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp.timerSeconds]);

  const loadServicesFor = async (phoneVal, networkVal) => {
    setStep("services");
    try {
      const services = await fetchSubscriptions(phoneVal, networkVal);
      setServicesList(services || []);
      if (!services || services.length === 0) {
        await pushAiText(text.noServices, 400);
      } else {
        await pushAiServices(services, 500);
      }
    } catch (err) {
      setServicesList([]);
      await pushAiText(text.servicesFailed, 400);
    }
    setStep("menu");
    await pushAiText(text.menuPrompt, 500);
  };

  const openChat = useCallback(async () => {
    setOpen(true);
    setMessages([]);
    setOtpDraft("");
    setServicesList([]);
    setCancelSvc(null);
    setComplaintSvc(null);
    setComplaintType(null);
    setComplaintDescDraft("");
    setComplaintError("");
    setFeedbackRating(null);
    setFeedbackCategory(null);
    setFeedbackCommentDraft("");
    otp.reset();
    cancelFlow.reset();

    if (skipAuth && initialPhone && initialNetwork) {
      setPhone(initialPhone);
      setNetwork(initialNetwork);
      setPhoneDraft("");
      await pushAiText(text.welcomeBack(initialPhone), 300);
      await loadServicesFor(initialPhone, initialNetwork);
      return;
    }

    setStep("phone");
    setPhoneDraft("");
    setPhone("");
    setNetwork("");
    await pushAiText(text.greeting, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipAuth, initialPhone, initialNetwork]);

  const closeChat = () => setOpen(false);

  const closeChatAndGoHome = () => {
    setOpen(false);
    navigate("/");
  };

  const handlePhoneSubmit = async (e) => {
    e && e.preventDefault();
    const cleaned = phoneDraft.replace(/\D/g, "").slice(0, 11);
    if (!cleaned || cleaned.length < 10) {
      addMessage("user", { type: "text", text: phoneDraft });
      setPhoneDraft("");
      await pushAiText(`⚠️ ${text.phoneInvalid}`);
      return;
    }
    addMessage("user", { type: "text", text: cleaned });
    setPhone(cleaned);
    setPhoneDraft("");
    await pushAiText(text.thanksPhone(cleaned));
    setStep("network");
  };

  const sendOtpFor = async (phoneVal, networkVal) => {
    try {
      await otp.sendOtp(phoneVal, networkVal);
      setOtpDraft("");
      await pushAiText(text.otpSentTo(phoneVal));
      setStep("otp");
    } catch (err) {
      await pushAiText(`⚠️ ${err.message || text.otpSendFailed}`);
      setStep("network");
    }
  };

  const handleNetworkSelect = async (n) => {
    addMessage("user", { type: "text", text: n });
    setNetwork(n);
    setStep("sending");
    await pushAiText(text.sendingOtp, 300);
    await sendOtpFor(phone, n);
  };

  const handleResend = async () => {
    await pushAiText(text.sendingOtp, 200);
    await sendOtpFor(phone, network);
  };

  const handleOtpSubmit = async (e) => {
    e && e.preventDefault();
    const code = otpDraft.trim();
    if (!code) {
      await pushAiText(`⚠️ ${text.otpInvalid}`, 200);
      return;
    }
    addMessage("user", { type: "text", text: "•".repeat(code.length) });
    setOtpDraft("");
    await pushAiText(text.verifying, 250);

    try {
      await otp.verifyOtp(phone, code);
      await pushAiText(text.verified, 400);
      await loadServicesFor(phone, network);
    } catch (err) {
      await pushAiText(`⚠️ ${err.message || text.verifyFailed}`);
    }
  };

  // ── Shared: end the flow after a task completes ──────────────────
  const endSession = async () => {
    setStep("ended");
    await pushAiText(text.sessionEnded, 500);
  };

  // ── Cancel & Refund — fully in-chat ──────────────────────────────

  const startCancelFlow = async () => {
    if (servicesList.length === 0) {
      await pushAiText(text.noServiceToCancel, 400);
      setStep("menu");
      await pushAiText(text.menuPrompt, 400);
      return;
    }
    if (servicesList.length === 1) {
      const only = servicesList[0];
      setCancelSvc(only);
      setStep("cancelReason");
      await pushAiText(text.whyCancel(only.name), 400);
      return;
    }
    setStep("cancelSelectService");
    await pushAiText(text.whichServiceCancel, 400);
  };

  const handleCancelServiceSelect = async (svc) => {
    addMessage("user", { type: "text", text: svc.name });
    setCancelSvc(svc);
    setStep("cancelReason");
    await pushAiText(text.whyCancel(svc.name), 400);
  };

  const handleCancelReasonSelect = async (reasonObj) => {
    const label = isEn ? reasonObj.labelEn : (reasonObj.labelRu || reasonObj.labelEn);
    addMessage("user", { type: "text", text: label });
    setStep("cancelProcessing");
    await pushAiText(text.cancelling, 300);

    try {
      await cancelFlow.submitCancel({
        svc: cancelSvc,
        phone,
        network,
        reason: reasonObj.id,
        language: isEn ? "English" : "Roman Urdu",
      });
      const deducted = cancelSvc?.totalDeducted || 0;
      const refundTotal = deducted * 2;
      await pushAiCancelResult(cancelSvc, refundTotal, 300);

      const cancelledName = cancelSvc?.name;
      const cancelledId = cancelSvc?.id;
      setServicesList((prev) => prev.filter((s) => s.id !== cancelledId));
      setCancelSvc(null);

      await pushAiText(text.cancelClosing(cancelledName), 400);
      await endSession();
    } catch (err) {
      await pushAiText(`⚠️ ${err.message || text.cancelFailed}`);
      setStep("cancelReason");
    }
  };

  // ── Complaint — fully in-chat ─────────────────────────────────────

  const startComplaintFlow = async () => {
    setComplaintSvc(null);
    setComplaintType(null);
    setComplaintDescDraft("");
    setComplaintError("");

    if (servicesList.length === 0) {
      setStep("complaintType");
      await pushAiText(text.whatWentWrong, 400);
      return;
    }
    setStep("complaintSelectService");
    await pushAiText(text.whichServiceComplaint, 400);
  };

  const handleComplaintServiceSelect = async (svc) => {
    addMessage("user", { type: "text", text: svc ? svc.name : text.generalComplaint });
    setComplaintSvc(svc);
    setStep("complaintType");
    await pushAiText(text.whatWentWrong, 400);
  };

  const handleComplaintTypeSelect = async (typeObj) => {
    const label = isEn ? typeObj.labelEn : (typeObj.labelRu || typeObj.labelEn);
    addMessage("user", { type: "text", text: `${typeObj.icon} ${label}` });
    setComplaintType(typeObj);
    setStep("complaintDescription");
    await pushAiText(text.describeIssue(label), 400);
  };

  const handleComplaintDescSubmit = async (e) => {
    e && e.preventDefault();
    const desc = complaintDescDraft.trim();
    if (!desc) {
      setComplaintError(text.complaintDescRequired);
      return;
    }
    setComplaintError("");
    addMessage("user", { type: "text", text: desc });
    setComplaintDescDraft("");
    setStep("complaintSubmitting");
    await pushAiText(text.submittingComplaint, 300);

    const typeLabel = isEn ? complaintType?.labelEn : (complaintType?.labelRu || complaintType?.labelEn);
    const serviceName = complaintSvc?.name || `${network || ""} ${isEn ? "Service" : "Service"}`.trim();

    try {
      const body = {
        phone,
        network,
        service_name: serviceName,
        type: complaintType?.id,
        description: desc,
        language: isEn ? "English" : "Roman Urdu",
      };

      const res = await fetch(apiUrl("/complaints"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || text.complaintFailed);
      }

      const complaintId = json.complaintId || json.id || ("WD" + Date.now().toString().slice(-6));
      await pushAiComplaintResult(complaintId, typeLabel, 300);
      await pushAiText(text.complaintClosing(complaintId), 500);
      await endSession();
    } catch (err) {
      await pushAiText(`⚠️ ${err.message || text.complaintFailed}`);
      setStep("complaintDescription");
    }
  };

  // ── Feedback — fully in-chat, mirrors Feedback.js page flow ────────

  const startFeedbackFlow = async () => {
    setFeedbackRating(null);
    setFeedbackCategory(null);
    setFeedbackCommentDraft("");
    setStep("feedbackRating");
    await pushAiText(
      isEn ? "I'd love to hear your feedback! How would you rate your overall experience with us?" : "Mujhe aap ki feedback sun kar khushi hogi! Aap hamare sath apna tajurba kaisa rate karenge?",
      400
    );
  };

  const handleFeedbackRatingSelect = async (ratingObj) => {
    const label = isEn ? ratingObj.labelEn : ratingObj.labelRu;
    addMessage("user", { type: "text", text: `${ratingObj.emoji} ${label}` });
    setFeedbackRating(ratingObj);
    setStep("feedbackCategory");
    await pushAiText(text.whatIsThisAbout, 400);
  };

  const handleFeedbackCategorySelect = async (catObj) => {
    const label = isEn ? catObj.labelEn : catObj.labelRu;
    addMessage("user", { type: "text", text: label });
    setFeedbackCategory(catObj);
    setStep("feedbackComment");
    await pushAiText(text.anyComments(label), 400);
  };

  const submitFeedback = async (commentVal) => {
    setStep("feedbackSubmitting");
    await pushAiText(text.submittingFeedback, 300);

    const serviceName = `${network || ""} ${isEn ? "Service" : "Service"}`.trim();
    const categoryLabel = isEn ? feedbackCategory?.labelEn : feedbackCategory?.labelRu;

    try {
      const body = {
        phone,
        network,
        service_name: serviceName,
        rating: feedbackRating?.val,
        category: feedbackCategory?.id,
        comment: commentVal,
        language: isEn ? "English" : "Roman Urdu",
      };

      const res = await fetch(apiUrl("/feedback"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || text.feedbackFailed);
      }

      const successMessage = json.message || (isEn ? "Thank you for your feedback." : "Aap ki feedback ka shukriya.");
      await pushAiFeedbackResult(feedbackRating, categoryLabel, successMessage, 300);
      await pushAiText(text.feedbackClosing, 500);
      await endSession();
    } catch (err) {
      await pushAiText(`⚠️ ${err.message || text.feedbackFailed}`);
      setStep("feedbackComment");
    }
  };

  const handleFeedbackCommentSubmit = async (e) => {
    e && e.preventDefault();
    const comment = feedbackCommentDraft.trim();
    if (comment) {
      addMessage("user", { type: "text", text: comment });
    }
    setFeedbackCommentDraft("");
    await submitFeedback(comment);
  };

  const handleFeedbackSkipComment = async () => {
    addMessage("user", { type: "text", text: text.skipComment });
    setFeedbackCommentDraft("");
    await submitFeedback("");
  };

  // ── Menu ───────────────────────────────────────────────────────────

  const handleMenuOption = async (opt) => {
    addMessage("user", { type: "text", text: opt.label });
    onMenuSelect && onMenuSelect(opt.id, { phone, network });

    if (opt.id === "cancel") {
      await startCancelFlow();
      return;
    }
    if (opt.id === "complaint") {
      await startComplaintFlow();
      return;
    }
    if (opt.id === "feedback") {
      await startFeedbackFlow();
      return;
    }
  };

  const menuOptions = (
    servicesList.length > 0
      ? [
          { id: "cancel", label: "🔄 Cancel & Refund" },
          { id: "complaint", label: "⚠️ Complaint" },
          { id: "feedback", label: "💚 Feedback" },
        ]
      : [
          { id: "complaint", label: "⚠️ Complaint" },
          { id: "feedback", label: "💚 Feedback" },
        ]
  );

  return (
    <>
      {children ? children(openChat) : null}

      {open && (
        <>
          <div onClick={closeChat} className="zara-backdrop" />
          <div className="zara-drawer">
            <div className="zara-header">
              <div className="zara-header-left">
                <div className="zara-avatar-wrap">
                  <img src={AIZzra} alt="ZARA" className="zara-avatar" />
                  <span className="zara-online-dot" />
                </div>
                <div>
                  <div className="zara-title">ZARA</div>
                  <div className="zara-subtitle">{isEn ? "Your support assistant" : "Aap ki support assistant"}</div>
                </div>
              </div>
              <button onClick={closeChat} className="zara-close-btn" aria-label="Close">×</button>
            </div>

            <div ref={bodyRef} className="zara-body">
              {messages.map((msg) => (
                <div key={msg.id} className={`zara-row ${msg.sender === "user" ? "zara-row-user" : "zara-row-ai"}`}>
                  {msg.sender === "ai" && (
                    <img src={AIZzra} alt="" className="zara-bubble-avatar" />
                  )}

                  {msg.type === "services" && (
                    <div className="zara-services-list">
                      {msg.services.map((svc) => (
                        <div key={svc.id} className="zara-service-card">
                          <span className="zara-service-icon">{svc.icon}</span>
                          <div className="zara-service-info">
                            <div className="zara-service-name">{svc.name}</div>
                            <div className="zara-service-meta">
                              {svc.status} • {isEn ? "since" : "se"} {svc.subscribedOn}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {msg.type === "cancelResult" && (
                    <div className="zara-result-card zara-result-green">
                      <div className="zara-result-head">
                        <span className="zara-result-emoji">✅</span>
                        <span className="zara-result-title">
                          {isEn ? `${msg.svc?.name} cancelled` : `${msg.svc?.name} cancel ho gayi`}
                        </span>
                      </div>
                      <p className="zara-result-footnote" style={{ color: "#2d6a4f" }}>
                        {isEn ? "Refund will reach your account in 3–5 business days." : "Refund 3–5 kaam ke dinon mein aap ke account mein aa jayega."}
                      </p>
                    </div>
                  )}

                  {msg.type === "complaintResult" && (
                    <div className="zara-result-card zara-result-orange">
                      <div className="zara-result-head">
                        <span className="zara-result-emoji">✅</span>
                        <span className="zara-result-title">
                          {isEn ? "Complaint registered" : "Complaint register ho gayi"}
                        </span>
                      </div>
                      <div className="zara-result-box" style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: "0.7rem", color: "#a04000", marginBottom: 2 }}>{isEn ? "Complaint ID" : "Complaint ID"}</div>
                        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#e67e22", letterSpacing: "1px" }}>#{msg.complaintId}</div>
                      </div>
                      <p className="zara-result-footnote" style={{ color: "#a04000" }}>
                        {isEn
                          ? `Type: ${msg.typeLabel}. Our team will follow up within 24–48 hours.`
                          : `Type: ${msg.typeLabel}. Hamari team 24–48 ghanton mein follow up karegi.`}
                      </p>
                    </div>
                  )}

                  {msg.type === "feedbackResult" && (
                    <div className="zara-result-card zara-result-green">
                      <div className="zara-result-head">
                        <span className="zara-result-emoji">{msg.ratingObj?.emoji}</span>
                        <span className="zara-result-title">
                          {isEn ? "Feedback submitted" : "Feedback submit ho gayi"}
                        </span>
                      </div>
                      <div className="zara-result-box">
                        <div className="zara-result-line">
                          <span>{isEn ? "Rating" : "Rating"}</span>
                          <span className="zara-result-line-value">
                            {msg.ratingObj?.val}/5 {isEn ? msg.ratingObj?.labelEn : msg.ratingObj?.labelRu}
                          </span>
                        </div>
                        <div className="zara-result-line">
                          <span>{isEn ? "Category" : "Category"}</span>
                          <span className="zara-result-line-value">{msg.categoryLabel}</span>
                        </div>
                      </div>
                      <p className="zara-result-footnote" style={{ color: "#1e8449" }}>
                        {msg.message}
                      </p>
                    </div>
                  )}

                  {msg.type === "text" && (
                    <div className={`zara-bubble ${msg.sender === "user" ? "zara-bubble-user" : "zara-bubble-ai"}`}>
                      {msg.text}
                    </div>
                  )}
                </div>
              ))}

              {typing && (
                <div className="zara-row zara-row-ai">
                  <img src={AIZzra} alt="" className="zara-bubble-avatar" />
                  <div className="zara-typing">
                    <span className="typing-dot">●</span> <span className="typing-dot">●</span> <span className="typing-dot">●</span>
                  </div>
                </div>
              )}
            </div>

            <div className="zara-input-area">
              {step === "phone" && (
                <form onSubmit={handlePhoneSubmit} className="zara-input-row">
                  <input type="tel" autoFocus className="form-control form-control-sm zara-input" placeholder="03XX-XXXXXXX"
                    value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value.replace(/\D/g, "").slice(0, 11))} />
                  <button type="submit" className="btn btn-sm btn-info text-white zara-send-btn">{isEn ? "Send" : "Bhejein"}</button>
                </form>
              )}

              {step === "network" && (
                <div className="zara-chip-row">
                  {NETWORKS.map((n) => (
                    <button key={n} type="button" className="zara-chip zara-chip-blue" onClick={() => handleNetworkSelect(n)}>
                      {n}
                    </button>
                  ))}
                </div>
              )}

              {(step === "sending" || step === "services" || step === "cancelProcessing" || step === "complaintSubmitting" || step === "feedbackSubmitting") && (
                <div className="zara-status-text">
                  {step === "sending" && (isEn ? "Sending OTP..." : "OTP bheja ja raha hai...")}
                  {step === "services" && (isEn ? "Loading your services..." : "Services load ho rahi hain...")}
                  {step === "cancelProcessing" && (isEn ? "Processing cancellation..." : "Cancellation process ho rahi hai...")}
                  {step === "complaintSubmitting" && (isEn ? "Submitting your complaint..." : "Complaint submit ho rahi hai...")}
                  {step === "feedbackSubmitting" && (isEn ? "Submitting your feedback..." : "Feedback submit ho rahi hai...")}
                </div>
              )}

              {step === "otp" && (
                <div>
                  <form onSubmit={handleOtpSubmit} className="zara-input-row" style={{ marginBottom: 8 }}>
                    <input type="tel" autoFocus className="form-control form-control-sm zara-input" placeholder={isEn ? "Enter OTP" : "OTP darj karein"}
                      value={otpDraft} onChange={(e) => setOtpDraft(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                    <button type="submit" disabled={otp.verifying} className="btn btn-sm btn-info text-white zara-send-btn">
                      {otp.verifying ? "..." : (isEn ? "Verify" : "Verify Karein")}
                    </button>
                  </form>
                  <div className="zara-otp-footer">
                    <span className="zara-timer">
                      {isEn ? "Expires in" : "Expire hoga"}: {otp.formattedTimer}
                    </span>
                    <button type="button" onClick={handleResend} disabled={otp.sending} className="zara-resend-btn">
                      {text.resend}
                    </button>
                  </div>
                </div>
              )}

              {step === "menu" && (
                <div className="zara-chip-row">
                  {menuOptions.map((opt) => (
                    <button key={opt.id} type="button" className="zara-chip zara-chip-light" onClick={() => handleMenuOption(opt)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {step === "cancelSelectService" && (
                <div className="zara-chip-row">
                  {servicesList.map((svc) => (
                    <button key={svc.id} type="button" className="zara-chip zara-chip-orange" onClick={() => handleCancelServiceSelect(svc)}>
                      {svc.icon} {svc.name}
                    </button>
                  ))}
                </div>
              )}

              {step === "cancelReason" && (
                <div className="zara-chip-row">
                  {cancelReasons.map((r) => (
                    <button key={r.id} type="button" className="zara-chip zara-chip-orange" onClick={() => handleCancelReasonSelect(r)}>
                      {isEn ? r.labelEn : (r.labelRu || r.labelEn)}
                    </button>
                  ))}
                </div>
              )}

              {step === "complaintSelectService" && (
                <div className="zara-chip-row">
                  {servicesList.map((svc) => (
                    <button key={svc.id} type="button" className="zara-chip zara-chip-blue-light" onClick={() => handleComplaintServiceSelect(svc)}>
                      {svc.icon} {svc.name}
                    </button>
                  ))}
                  <button type="button" className="zara-chip zara-chip-gray" onClick={() => handleComplaintServiceSelect(null)}>
                    {text.generalComplaint}
                  </button>
                </div>
              )}

              {step === "complaintType" && (
                <div className="zara-chip-row">
                  {COMPLAINT_TYPES.map((c) => (
                    <button key={c.id} type="button" className="zara-chip zara-chip-orange" onClick={() => handleComplaintTypeSelect(c)}>
                      {c.icon} {isEn ? c.labelEn : (c.labelRu || c.labelEn)}
                    </button>
                  ))}
                </div>
              )}

              {step === "complaintDescription" && (
                <form onSubmit={handleComplaintDescSubmit}>
                  <div className="zara-input-row" style={{ marginBottom: 4 }}>
                    <input
                      type="text"
                      autoFocus
                      className="form-control form-control-sm zara-input"
                      placeholder={isEn ? "Describe the issue..." : "Masla likhein..."}
                      value={complaintDescDraft}
                      onChange={(e) => { setComplaintDescDraft(e.target.value); if (complaintError) setComplaintError(""); }}
                    />
                    <button type="submit" className="zara-send-btn" style={{ background: "#e67e22", color: "white", border: "none" }}>
                      {isEn ? "Submit" : "Bhejein"}
                    </button>
                  </div>
                  {complaintError && (
                    <div className="zara-error-text">⚠️ {complaintError}</div>
                  )}
                </form>
              )}

              {step === "feedbackRating" && (
                <div className="zara-chip-row">
                  {FEEDBACK_RATINGS.map((r) => (
                    <button key={r.val} type="button" className="zara-chip zara-chip-green" onClick={() => handleFeedbackRatingSelect(r)}>
                      {r.emoji} {isEn ? r.labelEn : r.labelRu}
                    </button>
                  ))}
                </div>
              )}

              {step === "feedbackCategory" && (
                <div className="zara-chip-row">
                  {FEEDBACK_CATEGORIES.map((c) => (
                    <button key={c.id} type="button" className="zara-chip zara-chip-green" onClick={() => handleFeedbackCategorySelect(c)}>
                      {isEn ? c.labelEn : c.labelRu}
                    </button>
                  ))}
                </div>
              )}

              {step === "feedbackComment" && (
                <form onSubmit={handleFeedbackCommentSubmit}>
                  <div className="zara-input-row" style={{ marginBottom: 6, alignItems: "flex-start" }}>
                    <textarea
                      autoFocus
                      rows={2}
                      className="zara-textarea"
                      placeholder={isEn ? "Type your comments (optional)..." : "Apna comment likhein (optional)..."}
                      value={feedbackCommentDraft}
                      onChange={(e) => setFeedbackCommentDraft(e.target.value)}
                    />
                    <button type="submit" className="zara-send-btn" style={{ background: "#2ecc71", color: "white", border: "none" }}>
                      {isEn ? "Submit" : "Bhejein"}
                    </button>
                  </div>
                  <button type="button" onClick={handleFeedbackSkipComment} className="zara-resend-btn">
                    {text.skipComment}
                  </button>
                </form>
              )}

              {step === "ended" && (
                <div className="zara-end-wrap">
                  <button type="button" onClick={closeChatAndGoHome} className="zara-end-btn">
                    {text.closeChatBtn}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ZaraChatWidget;